// The sign-up gate, browser side.
//
// Turnstile is loaded and rendered explicitly rather than by the usual
// auto-scanning `class="cf-turnstile"` div, for one reason: the form must not
// be able to post without a token, and an implicit widget gives no promise to
// await. Here the submit handler asks for a token and gets one or gets an
// error — there is no third outcome in which the request quietly goes anyway.
//
// The widget is invisible. It does nothing at all until it is asked
// (`execution: "execute"`) and it never draws itself
// (`appearance: "interaction-only"`, on a sitekey whose widget type is
// Invisible, which never asks for interaction), so the landing page is a
// heading, a field and a button from first paint to last. Pressing Sign up runs
// the challenge in the background; the email is not posted until it passes.
//
// The trade this makes: an invisible widget has no fallback. A visitor
// Cloudflare cannot clear on the evidence available — a hardened privacy
// browser, some VPN exits, Tor — is simply refused, with no checkbox to click
// to prove otherwise. A Managed sitekey would offer that second chance at the
// cost of showing a box. This is the invisible one on purpose.

interface TurnstileOptions {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback": (code?: string) => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
  theme: "auto" | "light" | "dark";
  appearance: "always" | "execute" | "interaction-only";
  execution: "render" | "execute";
  size: "normal" | "flexible" | "compact";
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileOptions): string | undefined;
  execute(widgetId: string): void;
  reset(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_ID = "cf-turnstile-script";
const ONLOAD_CALLBACK = "__mindTheFenTurnstileReady";

/** Load Cloudflare's script once and resolve when its API is on `window`.
 *
 *  The `onload` parameter is how Turnstile says "explicit rendering is safe
 *  now"; waiting on the <script> element's own load event is a race, because
 *  the API object is published a tick later. */
function loadTurnstile(): Promise<TurnstileApi> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);

    const existing = document.getElementById(SCRIPT_ID);
    const settle = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile-unavailable"));
    };

    if (existing) {
      existing.addEventListener("load", settle, { once: true });
      existing.addEventListener("error", () => reject(new Error("turnstile-unavailable")), { once: true });
      return;
    }

    (window as unknown as Record<string, unknown>)[ONLOAD_CALLBACK] = settle;
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${ONLOAD_CALLBACK}`;
    script.async = true;
    script.defer = true;
    // A blocked or failed script is a gate that is not there. Reject, so the
    // caller refuses to post rather than posting without a token.
    script.addEventListener("error", () => reject(new Error("turnstile-unavailable")), { once: true });
    document.head.appendChild(script);
  });
}

/** One widget, and the promise the form waits on.
 *
 *  Showing and hiding the host is the caller's, not this class's: the landing
 *  page is a component now, and a class reaching in to set `hidden` on a node
 *  the renderer owns is exactly the kind of second writer that makes a DOM
 *  disagree with the state that produced it. This only ever fills the box.
 *
 *  A Turnstile token is single-use, so every attempt resets the widget and
 *  earns a fresh one. That is also what makes a failed send recoverable: the
 *  player presses Sign up again and gets a new checkbox rather than a token the
 *  server has already spent. */
export class SignUpGate {
  private readonly host: HTMLElement;
  private readonly siteKey: string;
  private widget?: Promise<{ api: TurnstileApi; id: string }>;
  private pending?: { resolve: (token: string) => void; reject: (error: Error) => void };

  constructor(host: HTMLElement, siteKey: string) {
    this.host = host;
    this.siteKey = siteKey;
  }

  /** Whether the page was served with a usable site key. False means the gate
   *  is missing, which the form treats as a reason not to submit. */
  get configured(): boolean {
    return this.siteKey !== "" && !this.siteKey.startsWith("__");
  }

  private mount(): Promise<{ api: TurnstileApi; id: string }> {
    this.widget ??= loadTurnstile().then((api) => {
      const id = api.render(this.host, {
        sitekey: this.siteKey,
        callback: (token) => this.settle((p) => p.resolve(token)),
        "error-callback": () => this.settle((p) => p.reject(new Error("turnstile-error"))),
        "expired-callback": () => this.settle((p) => p.reject(new Error("turnstile-expired"))),
        "timeout-callback": () => this.settle((p) => p.reject(new Error("turnstile-timeout"))),
        theme: "dark",
        // Never drawn. With an Invisible sitekey there is nothing to draw, and
        // this keeps it that way if a Managed key is ever configured by mistake
        // — such a key would then fail closed rather than quietly sprouting a
        // checkbox on a page that has no room for one.
        appearance: "interaction-only",
        execution: "execute",
        size: "flexible",
      });
      if (!id) throw new Error("turnstile-unavailable");
      return { api, id };
    });
    return this.widget;
  }

  private settle(apply: (pending: NonNullable<SignUpGate["pending"]>) => void): void {
    const pending = this.pending;
    if (!pending) return;
    this.pending = undefined;
    apply(pending);
  }

  /** Show the checkbox and resolve with the token the player earns by clearing
   *  it. Rejects if the widget cannot run or the challenge is not passed —
   *  never resolves with nothing. */
  async solve(): Promise<string> {
    if (!this.configured) throw new Error("turnstile-unavailable");
    const { api, id } = await this.mount();
    // Drop whatever the last attempt left behind: a spent token, a cleared
    // checkbox, or an error the player has already read.
    api.reset(id);
    this.settle((p) => p.reject(new Error("turnstile-superseded")));

    const token = new Promise<string>((resolve, reject) => {
      this.pending = { resolve, reject };
    });
    api.execute(id);
    return token;
  }

  /** Put the widget away once the attempt is over, whichever way it went. */
  async clear(): Promise<void> {
    if (!this.widget) return;
    const { api, id } = await this.widget.catch(() => ({ api: undefined, id: "" }));
    if (api) api.reset(id);
  }
}
